import z from 'zod/v4'
import type { ProductCatalogPrintInput } from './productCatalogPrint.types'

export type ProductCatalogPrintPreset = 'customer_catalog' | 'recipe_book'

const productCatalogPrintFieldsSchema = z.object({
  preset: z.enum(['customer_catalog', 'recipe_book']),
  source: z.enum(['all', 'collections']),
  collection_ids: z.array(z.number().int().positive()),
  include_description: z.boolean(),
  include_recipes: z.boolean(),
  include_prices: z.boolean(),
  include_cover: z.boolean(),
  layout: z.enum(['rows', 'full_page']),
  page_fill: z.enum(['preserve_order', 'optimize']),
  image_size: z.enum(['small', 'medium', 'large']),
})

/** The catalog builder's form values. */
export type ProductCatalogPrintFormValues = z.input<typeof productCatalogPrintFieldsSchema>

type ProductCatalogPrintFields = z.output<typeof productCatalogPrintFieldsSchema>

/** Form values the API accepts: printing collections needs at least one. */
export type ProductCatalogPrintValues = ProductCatalogPrintFields &
  ({ source: 'all' } | { source: 'collections'; collection_ids: [number, ...number[]] })

/** The option fields each preset sets. */
type ProductCatalogPrintPresetOptions = Pick<
  ProductCatalogPrintFormValues,
  'include_description' | 'include_recipes' | 'include_prices' | 'include_cover' | 'image_size'
>

export const productCatalogPrintPresets = {
  customer_catalog: {
    include_description: true,
    include_recipes: false,
    include_prices: true,
    include_cover: true,
    image_size: 'medium',
  },
  recipe_book: {
    include_description: false,
    include_recipes: true,
    include_prices: false,
    include_cover: false,
    image_size: 'small',
  },
} satisfies Record<ProductCatalogPrintPreset, ProductCatalogPrintPresetOptions>

export const productCatalogPrintSchema = productCatalogPrintFieldsSchema.refine(
  (value): value is ProductCatalogPrintValues =>
    value.source !== 'collections' || value.collection_ids.length > 0,
  {
    path: ['collection_ids'],
    message: 'Choose at least one collection to print.',
  }
)

export function getProductCatalogPrintDefaults(
  initialCollections: readonly { id: number | string }[] = []
): ProductCatalogPrintFormValues {
  return {
    preset: 'customer_catalog',
    source: initialCollections.length > 0 ? 'collections' : 'all',
    collection_ids: initialCollections.map((collection) => Number(collection.id)),
    layout: 'rows',
    page_fill: 'preserve_order',
    ...productCatalogPrintPresets.customer_catalog,
  }
}

export function toProductCatalogPrintPayload(
  values: ProductCatalogPrintValues
): ProductCatalogPrintInput {
  const { preset: _preset, ...payload } = values

  return payload.source === 'collections' ? payload : { ...payload, collection_ids: [] }
}
